/**
 * Section Preloading System
 * 
 * This module defines preload functions for lazy-loaded sections, organized by priority tiers.
 * Sections are preloaded based on usage frequency to improve navigation performance.
 */

import { QueryClient } from '@tanstack/react-query';
import {
  prefetchLeadsData,
  prefetchCustomersData,
  prefetchContractsData,
  prefetchAufgabenData,
  prefetchFremdvertraegeData,
  prefetchVertriebspartnerData,
  prefetchDownloadcenterData,
  prefetchProduktanbieterData,
  prefetchDashboardData,
  prefetchEventsData,
  prefetchCalendarData,
} from './prefetchData';

// Tier 1 - Highest Priority (used daily)
// These sections are preloaded first after the dashboard loads
export const tier1Sections = [
  () => import("@/components/sections/Leads"),
  () => import("@/components/sections/Customers"),
  () => import("@/components/sections/Contracts"),
  () => import("@/components/sections/MeineAufgaben"),
];

// Tier 2 - Medium Priority (used regularly)
// These sections are preloaded after Tier 1
export const tier2Sections = [
  () => import("@/components/sections/Fremdvertraege"),
  () => import("@/components/sections/Produktanbieter"),
  () => import("@/components/sections/Vertriebspartner"),
  () => import("@/components/sections/Downloadcenter"),
  () => import("@/components/sections/Contacts"),
  () => import("@/components/sections/ProduktanbieterDetail"),
];

// Tier 3 - Lower Priority (used occasionally)
// These sections are preloaded last
export const tier3Sections = [
  () => import("@/components/sections/Buchhaltung"),
  () => import("@/components/sections/Settings"),
  () => import("@/components/sections/ELearning"),
  () => import("@/components/sections/Email"),
  () => import("@/components/sections/HilfeSupport"),
  () => import("@/components/sections/Abrechnungen"),
  () => import("@/components/sections/RabatteSideletter"),
  () => import("@/components/sections/Webdialer"),
  () => import("@/components/sections/KfzVbs"),
  () => import("@/components/sections/LeadheldenBestelluebersicht"),
];

// Map section IDs to their preload functions for hover-triggered prefetching
export const sectionPreloadMap: Record<string, () => Promise<any>> = {
  'leads': () => import("@/components/sections/Leads"),
  'customers': () => import("@/components/sections/Customers"),
  'contracts': () => import("@/components/sections/Contracts"),
  'meine-aufgaben': () => import("@/components/sections/MeineAufgaben"),
  'fremdvertraege': () => import("@/components/sections/Fremdvertraege"),
  'produktanbieter': () => import("@/components/sections/Produktanbieter"),
  'vertriebspartner': () => import("@/components/sections/Vertriebspartner"),
  'downloadcenter': () => import("@/components/sections/Downloadcenter"),
  'contacts': () => import("@/components/sections/Contacts"),
  'buchhaltung': () => import("@/components/sections/Buchhaltung"),
  'einstellungen': () => import("@/components/sections/Settings"),
  'elearning': () => import("@/components/sections/ELearning"),
  'email': () => import("@/components/sections/Email"),
  'hilfe-support': () => import("@/components/sections/HilfeSupport"),
  'abrechnungen': () => import("@/components/sections/Abrechnungen"),
  'rabatte-sideletter': () => import("@/components/sections/RabatteSideletter"),
  'webdialer': () => import("@/components/sections/Webdialer"),
  'kfz-vbs': () => import("@/components/sections/KfzVbs"),
  'leadhelden-bestelluebersicht': () => import("@/components/sections/LeadheldenBestelluebersicht"),
};

// Map section IDs to their data prefetch functions for hover-triggered data loading
// Note: fremdvertraege requires userEmail, so it's wrapped with a no-op fallback
export const sectionDataPrefetchMap: Record<string, (queryClient: QueryClient, userId: string, userEmail?: string) => void> = {
  'dashboard': (queryClient, userId) => {
    prefetchDashboardData(queryClient, userId);
    prefetchEventsData(queryClient, userId);
    prefetchCalendarData(queryClient, userId);
  },
  'leads': prefetchLeadsData,
  'customers': prefetchCustomersData,
  'contracts': prefetchContractsData,
  'meine-aufgaben': prefetchAufgabenData,
  'fremdvertraege': (queryClient, userId, userEmail) => {
    if (userEmail) {
      prefetchFremdvertraegeData(queryClient, userId, userEmail);
    }
  },
  'vertriebspartner': prefetchVertriebspartnerData,
  'downloadcenter': prefetchDownloadcenterData,
  'produktanbieter': prefetchProduktanbieterData,
};
